import { useState, useMemo } from 'react'
import { router } from '@inertiajs/react'
import { ChevronRight, LogOut, Plus, Search } from 'lucide-react'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { AccountWithType } from '@/types'

type SelectorProps = {
  accounts: AccountWithType[]
}

export default function AccountsSelector({ accounts }: SelectorProps) {
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    if (!search.trim()) return accounts
    const q = search.toLowerCase()
    return accounts.filter(a => a.name.toLowerCase().includes(q))
  }, [accounts, search])

  return (
    <AuthLayout
      title="Workspaces"
      heading="Your workspaces"
      description={
        accounts.length === 0
          ? "Create a workspace to start forming your business."
          : "Pick a workspace to continue."
      }
    >
      {accounts.length === 0 ? (
        <div className="text-center py-10">
          <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
            <Plus className="w-6 h-6 text-muted-foreground" />
          </div>
          <p className="text-[13px] text-muted-foreground mb-5">
            You don't have any workspaces yet.
          </p>
          <Button asChild className="h-11 px-6 text-sm font-semibold">
            <a href="/registration/complete">Create workspace</a>
          </Button>
        </div>
      ) : (
        <>
          {accounts.length > 5 && (
            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder="Search workspaces..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>
          )}
          <div className="space-y-0.5">
            {filtered.map(account => (
              <a
                key={account.id}
                href={`/${account.slug}/businesses`}
                className="flex items-center gap-3 px-2.5 py-2 rounded-lg hover:bg-muted/50 transition-colors group"
              >
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="text-xs font-bold text-primary">
                    {account.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <p className="flex-1 min-w-0 text-sm font-medium truncate">{account.name}</p>
                <Badge
                  variant={account.type === 'IndividualAccount' ? 'secondary' : 'default'}
                  className="shrink-0 text-[10px] font-medium"
                >
                  {account.type === 'IndividualAccount' ? 'Personal' : 'Team'}
                </Badge>
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />
              </a>
            ))}
            {filtered.length === 0 && search && (
              <p className="text-center text-sm text-muted-foreground py-4">No workspaces match "{search}"</p>
            )}
          </div>
        </>
      )}

      <div className="mt-8 pt-5 border-t border-border/60">
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-[13px] text-muted-foreground hover:text-foreground"
          onClick={() => router.delete('/session')}
        >
          <LogOut className="w-3.5 h-3.5 mr-2" />
          Sign out
        </Button>
      </div>
    </AuthLayout>
  )
}
